import { StateHandler } from 'oskari-ui/util';
import { Helper } from './Helper';
import { DrawingHelper } from './DrawingHelper';
import { confirmEdit } from './EditConfirmation';
export class FeatureEditorPanelHandler extends StateHandler {
  constructor() {
    super();
    this.name = 'FeatureEditor';
    this.sandbox = Oskari.getSandbox();
    this.mapModule = this.sandbox.findRegisteredModuleInstance('MainMapModule');
    this.mapLayerService = this.getSandbox().getService('Oskari.mapframework.service.MapLayerService');
    this.state = {
      currentLayer: null,
      feature: null,
      loading: false,
      hasPendingChanges: false
    };
    this.eventHandlers = {
      FeatureEvent: function (event) {
        if (event.getOperation() !== 'click') {
          return;
        }
        const currentLayer = this.getCurrentLayer();
        if (!currentLayer) {
          return;
        }
        const currentLayerId = currentLayer.id;
        const editLayerFeatures = event.getFeatures().filter(f => f.layerId === currentLayerId);
        if (!editLayerFeatures.length) {
          // no features hit on layer that we are currently editing
          return;
        }
        const clickedFeature = editLayerFeatures[0].geojson.features[0];
        this.selectFeatureOnMap(currentLayerId, clickedFeature?.id);
        // found one -> edit it
        this.editFeature(clickedFeature);
      }
    };
  }
  getSandbox() {
    return this.sandbox;
  }

  /**
   * @method onEvent
   * Event is handled forwarded to correct #eventHandlers if found or discarded if not.
   * @param {Oskari.mapframework.event.Event} event a Oskari event object
   */
  onEvent(event) {
    const handler = this.eventHandlers[event.getName()];
    if (!handler) {
      return;
    }
    return handler.apply(this, [event]);
  }
  init(layerId, featureId) {
    // unregister eventHandlers just in case this is a re-init
    Object.keys(this.eventHandlers).forEach(eventName => {
      this.getSandbox().unregisterFromEventByName(this, eventName);
    });

    // register again
    Object.keys(this.eventHandlers).forEach(eventName => {
      this.getSandbox().registerForEventByName(this, eventName);
    });
    Helper.describeLayer(layerId).then(metadata => {
      this.getSandbox().postRequestByName('MapModulePlugin.GetFeatureInfoActivationRequest', [false, this.getName()]);
      this.setCurrentLayer(layerId, metadata.geometryType, metadata.types);
      if (featureId) {
        this.updateCurrentFeature(layerId, featureId);
      }
      return;
    }).catch(() => {
      this.setLoading(false);
    });
  }

  /**
   * Destroys/removes this view from the screen.
   * @method @public destroy
   */
  destroy() {
    Object.keys(this.eventHandlers).forEach(eventName => {
      this.getSandbox().unregisterFromEventByName(this, eventName);
    });
    this.stateListeners = [];
    // remove selection
    this.selectFeatureOnMap();
    this.updateState({
      currentLayer: null,
      feature: null
    });
    DrawingHelper.stopDrawing();
    // Restore layers hidden when the editor was started
    this._tempHiddenLayers.forEach(layer => this.changeLayerVisibility(layer.getId(), true));
    this._tempHiddenLayers = null;
    this.sandbox.postRequestByName('MapModulePlugin.GetFeatureInfoActivationRequest', [true, this.getName()]);
  }

  /**
   * @method @public getName
   * @return {String} the name for the component
   */
  getName() {
    return this.name;
  }
  getCurrentLayer() {
    return this.getState().currentLayer;
  }
  setCurrentLayer(layerId, geometryType, types) {
    const mapLayer = this.mapLayerService.findMapLayer(layerId);
    const fieldLabels = mapLayer.getAttributes('data')?.locale?.[Oskari.getLang()] || {};
    this.getSandbox().postRequestByName('AddMapLayerRequest', [layerId]);
    this.hideOtherVectorLayers(layerId);
    const newState = {
      id: layerId,
      geometryType: geometryType,
      fieldTypes: types,
      fieldLabels,
      name: mapLayer.getName(Oskari.getDefaultLanguage())
    };
    this.updateState({
      currentLayer: newState
    });
  }

  /**
   * update current feature to state. Fetch from map by given featureid
   * @param {*} featureId the id we received for the feature we're modifying
   * @param {*} layerId sanity checking the layerid remained the same
   */
  updateCurrentFeature(layerId, featureId) {
    // TODO: check if it is anyhow possible for the layerid to be something different besides currentlayer
    const {
      currentLayer
    } = this.getState();
    if (!currentLayer || currentLayer.id !== layerId) {
      return;
    }
    if (featureId) {
      const featuresMap = this.mapModule.getVectorFeatures(null, {
        layers: [layerId]
      });
      const features = featuresMap[layerId] ? featuresMap[layerId].features : null;
      const feature = features?.filter(feature => feature.id === featureId)?.[0] ?? null;
      this.setFeature(feature);
      this.selectFeatureOnMap(layerId, featureId);
    }
  }
  selectFeatureOnMap(layerId, featureId) {
    if (!layerId) {
      const {
        currentLayer
      } = this.getState();
      layerId = currentLayer.id;
    }
    const selectionService = this.getSandbox().getService('Oskari.mapframework.service.VectorFeatureSelectionService');
    if (!selectionService) {
      return;
    }
    if (!featureId) {
      selectionService.setSelectedFeatureIds(layerId, []);
    } else {
      selectionService.setSelectedFeatureIds(layerId, [featureId]);
    }
  }
  setFeature(feature) {
    this.updateState({
      feature
    });
  }
  getFeature() {
    return this.getState().feature;
  }
  setLoading(loading) {
    this.updateState({
      loading
    });
  }
  getLoading() {
    return this.getState().loading;
  }
  setHasPendingChanges(hasPendingChanges) {
    if (this.getState().hasPendingChanges === hasPendingChanges) {
      return;
    }
    this.updateState({
      hasPendingChanges
    });
  }

  /**
   * Temporarily hides layers from map that the user isn't editing
   * @method hideOtherVectorLayers
   */
  hideOtherVectorLayers(layerId) {
    const visibleSelectedLayers = this.getSandbox().findAllSelectedMapLayers().filter(layer => layer.isVisible());
    const layersToHide = visibleSelectedLayers.filter(layer => layer.getId() !== layerId).filter(layer => layer.isLayerOfType('WFS'));
    // hide other WFS layers that are visible on the map
    layersToHide.forEach(layer => this.changeLayerVisibility(layer.getId(), false));
    this._tempHiddenLayers = layersToHide;
  }
  changeLayerVisibility(layerId, isVisible) {
    this.getSandbox().postRequestByName('MapModulePlugin.MapLayerVisibilityRequest', [layerId, isVisible]);
  }
  startNewFeature() {
    this.editFeature({
      type: 'Feature',
      properties: {}
    });
  }
  editFeature(geojson, confirmed) {
    if (typeof geojson === 'undefined') {
      // reset feature we were editing
      this.updateState({
        feature: null
      });
      return;
    }
    const {
      hasPendingChanges
    } = this.getState();
    if (!confirmed && this.getFeature()?.id !== geojson.id && hasPendingChanges) {
      confirmEdit(this.loc, () => this.editFeature(geojson, true));
    } else {
      // remove _oid (internal normalized id by Oskari) from properties
      delete geojson.properties?.oid;
      const {
        ...rest
      } = geojson.properties;
      const feature = {
        ...geojson,
        properties: {
          ...rest
        }
      };
      this.updateState({
        feature,
        hasPendingChanges: false
      });
    }
  }
}